import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import eventValue from 'teacher-ui-client/helpers/event-value'
import { on } from '@ember/modifier'
import { eq, not } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { includes } from 'teacher-ui-client/helpers/includes'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'

interface PagingInfoSignature {
  Args: {
    pageCount: number
    currentPage?: number
    itemCount?: number
    itemsPerPage?: number
    changePerPage?: (itemsPerPage: number) => void
    lockPerPage?: boolean
    itemsPerPageKey: string
    pageNumberKey: string
  }
  Element: HTMLDivElement
}

export class PagingInfo extends Component<PagingInfoSignature> {
  get itemRange() {
    const { itemCount, itemsPerPage, currentPage } = this.args
    if (itemCount === undefined || itemsPerPage === undefined || currentPage === undefined) {
      return null
    }

    const start = (currentPage - 1) * itemsPerPage + 1
    const end = Math.min(currentPage * itemsPerPage, itemCount)
    return `${start}-${end}`
  }

  @service router!: RouterService

  changePerPage = (perPage: number) => {
    if (this.args.changePerPage) {
      return this.args.changePerPage(perPage)
    }

    return this.router.transitionTo({
      queryParams: {
        [this.args.itemsPerPageKey]: perPage,
        [this.args.pageNumberKey]: 1, // Reset to first page when changing items per page
      },
    })
  }

  <template>
    <div class="flex items-center justify-between" ...attributes>
      {{#if this.itemRange}}
        <span class="text-neutral-250 text-sm">{{t
            "components.pagination.showing"
            itemRange=this.itemRange
            end=@itemCount
          }}
        </span>
      {{/if}}
      {{#if @itemsPerPage}}
        {{#if @lockPerPage}}
          <PerPageLabel @itemsPerPage={{@itemsPerPage}} class="ml-auto" />
        {{else}}
          <PerPageSelector @changePerPage={{this.changePerPage}} @itemsPerPage={{@itemsPerPage}} class="ml-auto" />
        {{/if}}
      {{/if}}
    </div>
  </template>
}

export default PagingInfo

interface PerPageSelectorSignature {
  Args: {
    changePerPage: (itemsPerPage: number) => void
    itemsPerPage: number
  }
  Element: HTMLDivElement
}

const defaultPageSteps = [10, 25, 50, 100]

export const PerPageSelector: TOC<PerPageSelectorSignature> = <template>
  <div class="inline-flex items-center gap-2" ...attributes>
    <label class="text-neutral-250 text-sm font-normal" for="page-selector">{{t
        "components.pagination.itemsPerPage"
      }}</label>
    <select
      id="page-selector"
      disabled={{Boolean (not @changePerPage)}}
      class="border-neutral-75 focus:border-neutral-75 focus-visible:outline-blue-325 disabled:text-neutral-250 block rounded-lg border bg-transparent px-3 py-2 focus:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid disabled:bg-neutral-50"
      {{on "input" (eventValue @changePerPage)}}
    >
      {{#unless (includes defaultPageSteps @itemsPerPage)}}
        <option value={{@itemsPerPage}} selected>
          {{@itemsPerPage}}
        </option>
      {{/unless}}
      {{#each defaultPageSteps as |step|}}
        <option value={{step}} selected={{eq @itemsPerPage step}}>
          {{step}}
        </option>
      {{/each}}
    </select>
  </div>
</template>

interface PerPageLabelSignature {
  Args: {
    itemsPerPage: number
  }
  Element: HTMLSpanElement
}

export const PerPageLabel: TOC<PerPageLabelSignature> = <template>
  <span class="text-neutral-250 text-sm" ...attributes>
    {{t "components.pagination.itemsPerPage"}}:
    {{@itemsPerPage}}
  </span>
</template>
